import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'
import Select from 'react-select'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react'
import { CustomerContext } from '../../../contexts/CustomerContext'
import { ShootingSchedule, Cart } from '../../../models/_models'
import { collectBookingErrors, bookingErrorMessage } from './booking-errors'
import AppointmentLocationInfo from '../../appointment-location-info'
import AttentionBanner from '../../attention-banner'
import ShootingSchedules from '../../shooting-schedules'
import { isShoobDayKey } from '../../shooting-schedules/shoob-day'
import Button from '../../Button'
import Typography from '../../Typography'
import classes from './appointment-selection.module.sass'
import { routes } from '../../../routes'
import DressCodeInfo from '../../dress-code-info'
import useQueryString from '../../../hooks/use-query-string'
import { customStyles } from '../schedule-appointment'
import apptImgElem from './Senior_Sample.png';
import axios from 'axios';

const useShootingSchedules = (school, student, option) => {
  const [shootingSchedules, setShootingSchedules] = useState([])
  const [jobs, setJobs] = useState([]);
  const [shootingDates,setShootingDates] = useState([])

  const [schoolType, setSchoolType] = useState(null);

  useEffect(() => {
    if (school && school.attributes && school.attributes.schoolTypeId) {
      setSchoolType(school.attributes.schoolTypeId);
    }
  }, [school]);

  useEffect(() => {
    const fetchJobs = async () => {
      const grade = parseInt(student?.grade);
      if (grade === 11 || grade === 12)
      {
        const response = await axios.get('/djobs/instudio_seniors2?id=' + school.id);
        setJobs(response.data);
      } else {
        const response = await axios.get('/djobs/instudio2?id=' + school.id);
        setJobs(response.data);
      }
    };

    fetchJobs();
  }, [school, student]);

  useEffect(() => {
    if (jobs && jobs.length > 0 ) {
      setShootingDates(jobs.map(job => job.date.split('T')[0]));
    }  else {
      setShootingDates([]);
    }
  }, [jobs]);

  useEffect(() => {
    ShootingSchedule
    .where({ inFuture: true, schoolId: [school.id, 1075] })
      .order('startTime')
      .all()
      .then((res) => setShootingSchedules(res.data))
  }, [school.id,shootingDates])

  // Fetch the option-aware, span-checked bookable start-times (server is the
  // single source of truth). Without an option we can't compute them, so skip.
  useEffect(() => {
    if (!option || !option.id) return

    ShootingSchedule
      .selectExtra(['bookableTimeslots'])
      .where({ inFuture: true, schoolId: [school.id, 1075], optionId: option.id })
      .order('startTime')
      .all()
      .then((res) => setShootingSchedules(res.data))
  }, [shootingSchedules.length, option && option.id])

  return shootingSchedules.filter(schedule => {
    if (shootingDates.length === 0) return true;
    const scheduleDate = (schedule.shootingDay || '').split('T')[0];
    return shootingDates.includes(scheduleDate);
  })
}

// Demo mode for design preview only — renders the same dropdown structure
// as <ShootingSchedules> but with hard-coded sample dates and times. Activated
// via `?demo=true` so styling can be reviewed without real schedule data.
const DEMO_DAY_OPTIONS = [
  { value: '2026-09-08', label: 'September, 8' },
  { value: '2026-09-09', label: 'September, 9' },
  { value: '2026-09-10', label: 'September, 10' },
  { value: '2026-09-11', label: 'September, 11' },
  { value: '2026-09-15', label: 'September, 15' },
  { value: '2026-09-16', label: 'September, 16' },
  { value: '2026-09-22', label: 'September, 22' },
  { value: '2026-09-29', label: 'September, 29' },
  { value: '2026-10-03 @ Shoob', label: 'October, 3 @ Shoob Photo Studio' },
  { value: '2026-10-10 @ Shoob', label: 'October, 10 @ Shoob Photo Studio' },
]

// Generates a 15-minute-interval timeslot list between start and end (hour, 24h).
const buildTimes = (startHour, endHour) => {
  const out = []
  for (let h = startHour; h < endHour; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const start = `${String(((h + 11) % 12) + 1).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
      const endMin = m + 15
      const endH = endMin === 60 ? h + 1 : h
      const endM = endMin === 60 ? 0 : endMin
      const end = `${String(((endH + 11) % 12) + 1).padStart(2, '0')}:${String(endM).padStart(2, '0')} ${endH < 12 ? 'AM' : 'PM'}`
      out.push({ value: start, label: `${start} - ${end}` })
    }
  }
  return out
}

const DEMO_TIMES_BY_DAY = {
  '2026-09-08':           buildTimes(8, 16),        // 9 AM – 4 PM (full day)
  '2026-09-09':           buildTimes(9, 15),        // partial day
  '2026-09-10':           buildTimes(13, 19),       // afternoon
  '2026-09-11':           buildTimes(8, 12),        // morning only
  '2026-09-15':           buildTimes(9, 17),        // full day
  '2026-09-16':           buildTimes(10, 14),       // half day
  '2026-09-22':           [],                       // intentionally empty — shows the "no times" message
  '2026-09-29':           buildTimes(8, 16),
  '2026-10-03 @ Shoob':   buildTimes(11, 16),
  '2026-10-10 @ Shoob':   buildTimes(10, 17),
}

const SeniorPickerDemo = ({ setAppt, setHasSelectedDay, setIsApptAtShoob }) => {
  const [selectedDay, setSelectedDay] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const times = selectedDay ? DEMO_TIMES_BY_DAY[selectedDay.value] || [] : []

  useEffect(() => {
    setHasSelectedDay(!!selectedDay)
    setIsApptAtShoob(isShoobDayKey(selectedDay && selectedDay.value))
  }, [selectedDay])

  useEffect(() => {
    if (selectedDay && selectedTime) setAppt(`${selectedDay.value} ${selectedTime.value}`)
    else setAppt(null)
  }, [selectedDay, selectedTime])

  return (
    <div>
      <label>
        <Typography>Select Date</Typography>
        <Select
          styles={customStyles}
          placeholder="Choose one"
          value={selectedDay}
          onChange={(opt) => { setSelectedDay(opt); setSelectedTime(null) }}
          options={DEMO_DAY_OPTIONS}
        />
      </label>
      {selectedDay && (
        <>
          <Typography>Select Time</Typography>
          <Select
            styles={customStyles}
            placeholder="Choose one"
            noOptionsMessage={() => 'No appointments are available on this date. Please try another day.'}
            value={selectedTime}
            onChange={(opt) => setSelectedTime(opt)}
            options={times}
          />
        </>
      )}
    </div>
  )
}

// School id 1075 stands in for the in-house studio (see reformat-schedules.js);
// same name/address already shown at Checkout & OrderComplete for that schoolId
// -- keep this in sync with those if it ever changes.
const SHOOB_STUDIO_NAME = 'Shoob Studio'
const SHOOB_STUDIO_ADDRESS = '4640 Spyres Way #1, Modesto, CA'

// Prominent location card — shown above the date picker once a date is chosen,
// so the customer knows where the session will happen before they pick when.
// Only shown after a date is picked because the location depends on which day
// was selected (a school day vs. a Shoob-studio day for school 1075).
const SeniorLocationCard = ({ school, isAtShoob }) => {
  if (!school) return null
  const name = isAtShoob ? SHOOB_STUDIO_NAME : school.name
  const address = isAtShoob ? SHOOB_STUDIO_ADDRESS : school.scheduleLocation
  return (
    <div className={classes.locationCard}>
      <div className={classes.locationIconWrap} aria-hidden="true">
        <svg className={classes.locationIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>
      <div className={classes.locationContent}>
        <p className={classes.locationLabel}>Your session will be at</p>
        <p className={classes.locationName}>{name}</p>
        {address && (
          <p className={classes.locationAddress}>{address}</p>
        )}
      </div>
    </div>
  )
}

// 4-step progress indicator — step 3 active here (date/time picker).
const SeniorSteps = () => (
  <ol className={classes.steps} aria-label="Booking progress">
    <li className={cn(classes.stepItem, classes.stepItemDone)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Find your student</span>
    </li>
    <span className={cn(classes.stepConnector, classes.stepConnectorDone)} aria-hidden="true" />
    <li className={cn(classes.stepItem, classes.stepItemDone)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose your session</span>
    </li>
    <span className={cn(classes.stepConnector, classes.stepConnectorDone)} aria-hidden="true" />
    <li className={cn(classes.stepItem, classes.stepItemActive)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
          <line x1="8" y1="3" x2="8" y2="7"/>
          <line x1="16" y1="3" x2="16" y2="7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose date/time</span>
    </li>
    <span className={classes.stepConnector} aria-hidden="true" />
    <li className={classes.stepItem}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Confirm</span>
    </li>
  </ol>
)

const AppointmentSelection = () => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const { senior, demo, reschedule } = useQueryString()
  const isReschedule = !!reschedule
  const shootingSchedules = useShootingSchedules(
    customer.currentSchool, customer.currentStudent, customer.currentOrderPackage && customer.currentOrderPackage.option
  )
  const [appt, setAppt] = useState()
  const [isApptAtShoob, setIsApptAtShoob] = useState(false)
  const [hasSelectedDay, setHasSelectedDay] = useState(false)
  const [rescheduledAppt, setRescheduledAppt] = useState(null)

  // Save a spraypaint model, normalizing a thrown rejection into the same
  // { ok, err } shape as a validation failure so callers handle both.
  const trySave = async (model) => {
    try {
      return { ok: await model.save(), err: null }
    } catch (err) {
      return { ok: false, err }
    }
  }

  // Surface + log the real reason a booking step failed, then toast it. The old
  // code swallowed the error and told the customer to "refresh" (which never
  // helps); we now log to Sentry and show the actual server message.
  const reportBookingError = (orderPackage, action, err) => {
    const errors = collectBookingErrors(orderPackage, err)
    // eslint-disable-next-line no-console
    console.error(`Senior appointment: failed to ${action}`, { errors, modelErrors: orderPackage && orderPackage.errors, err })
    Sentry.captureMessage(`Senior appt booking failed (${action}): ${errors.join('; ') || 'unknown'}`)
    if (err) Sentry.captureException(err)
    toast(bookingErrorMessage(action, errors), { type: 'error' })
  }

  // The add-to-cart step can fail because the session is bound to a stale /
  // already-purchased cart (the cause of "refreshing never helps but a fresh
  // incognito session does"). Mint a brand-new cart so we can retry on a clean
  // one -- the exact thing incognito does for the customer manually.
  const makeFreshCart = async () => {
    try {
      const freshCart = new Cart({
        email: customer.email,
        schoolId: customer.currentSchool.id,
        createdLocation: 'sittings',
      })
      const { ok } = await trySave(freshCart)
      if (ok) {
        Sentry.captureMessage('Senior appt: reset cart and retried after add-to-cart failure', 'info')
        return freshCart
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Senior appointment: fresh-cart recovery failed', err)
    }
    return null
  }

  // Two-step booking: (1) add the sitting to the cart, then (2) attach the chosen
  // time (the server validates availability on step 2). A step-1 failure gets one
  // fresh-cart retry; a step-2 failure is a real availability conflict, so we
  // surface the reason instead of retrying.
  const bookSitting = async (orderPackage, cart, allowCartReset) => {
    orderPackage.cartId = cart.id
    orderPackage.schoolId = isApptAtShoob ? 1075 : customer.currentSchool.id

    const step1 = await trySave(orderPackage)
    if (!step1.ok) {
      if (allowCartReset) {
        const freshCart = await makeFreshCart()
        if (freshCart) {
          setCustomer({ ...customer, apptCart: freshCart })
          return bookSitting(orderPackage, freshCart, false) // retry once on a clean cart
        }
      }
      return reportBookingError(orderPackage, 'add your sitting to the cart', step1.err)
    }

    orderPackage.appointmentSeason = 'senior'
    orderPackage.appointmentTime = appt
    const step2 = await trySave(orderPackage)
    if (!step2.ok) {
      return reportBookingError(orderPackage, 'reserve your time', step2.err)
    }

    return history.push(routes.checkout({ app: true, ...(senior ? { senior: 'true' } : {}) }))
  }

  // Reschedule mode: move the customer's EXISTING appointment to the new time by
  // PATCHing its appointment_time. The server's appt_time_available validation
  // (self-excluded for a persisted record) reserves the new slot and frees the
  // old one. No payment -- a free time move. On success we show a confirmation.
  const rescheduleSitting = async (orderPackage) => {
    orderPackage.appointmentTime = appt
    orderPackage.appointmentSeason = orderPackage.appointmentSeason || 'senior'
    const res = await trySave(orderPackage)
    if (!res.ok) {
      return reportBookingError(orderPackage, 'move your appointment', res.err)
    }
    return setRescheduledAppt(appt)
  }

  const handleContinue = () => {
    if (!appt) {
      toast('Please choose a date and time before continuing.', { type: 'error' })
      return
    }

    if (isReschedule) {
      if (!customer.currentOrderPackage) {
        toast('We could not find your appointment. Please start again from Reschedule Appointment.', { type: 'error' })
        return
      }
      rescheduleSitting(customer.currentOrderPackage)
      return
    }

    // In demo/dev mode, the cart and order package are missing or stale, so
    // skip the API saves and jump straight to the next page so the design
    // flow can be reviewed.
    const isLocalhost = typeof window !== 'undefined'
      && /^(localhost|127\.|.*\.local)$/.test(window.location.hostname)
    if (demo || isLocalhost || !customer.currentOrderPackage || !customer.apptCart) {
      // Forward the picked time so the localhost Checkout demo summary can
      // reflect the actual selection instead of the hardcoded placeholder.
      history.push(routes.checkout({
        app: true,
        ...(senior ? { senior: 'true' } : {}),
        ...(appt ? { demoTime: appt } : {}),
      }))
      return
    }

    bookSitting(customer.currentOrderPackage, customer.apptCart, true)
  }

  // Reschedule confirmation — shown after a successful move (no checkout step).
  if (rescheduledAppt) {
    return (
      <div className={classes.seniorWrap}>
        <div className={classes.seniorPicker} style={{ textAlign: 'center' }}>
          <p className={classes.seniorPickerTitle}>Your appointment has been moved</p>
          <p style={{ fontSize: 18, margin: '8px 0 4px' }}>
            <strong>{customer.currentStudent?.firstName} {customer.currentStudent?.lastName}</strong>
          </p>
          <p style={{ fontSize: 18, margin: '0 0 16px' }}>New time: <strong>{rescheduledAppt}</strong></p>
          <p style={{ color: '#555' }}>
            A confirmation will be sent to {customer.email}. Need anything else? Contact us at
            {' '}<a href="mailto:info@shoobphoto.com">info@shoobphoto.com</a>.
          </p>
        </div>
      </div>
    )
  }

  // Senior flow — restyled to match the choose-your-session page.
  if (senior) {
    const hasSchedules = shootingSchedules.length > 0
    // Auto-enable demo data on localhost so the picker design is visible
    // during development even when prod has no senior schedules for this
    // school. `?demo=true` forces it on regardless of host.
    const isLocalhost = typeof window !== 'undefined'
      && /^(localhost|127\.|.*\.local)$/.test(window.location.hostname)
    const showDemo = !!demo || (isLocalhost && !hasSchedules)
    return (
      <div className={classes.seniorWrap}>
        <ToastContainer position="bottom-left" />
        <SeniorSteps />
        {hasSelectedDay && (
          <SeniorLocationCard school={customer.currentSchool} isAtShoob={isApptAtShoob} />
        )}
        <div className={classes.seniorPicker}>
          <p className={classes.seniorPickerTitle}>
            {isReschedule ? 'Pick a new date & time' : 'Pick a date & time'}
          </p>
          {showDemo ? (
            <SeniorPickerDemo setAppt={setAppt} setHasSelectedDay={setHasSelectedDay} setIsApptAtShoob={setIsApptAtShoob} />
          ) : hasSchedules ? (
            <ShootingSchedules
              school={customer.currentSchool}
              schedules={shootingSchedules}
              setAppt={setAppt}
              selectedOption={customer.currentOrderPackage.option}
              setIsApptAtShoob={setIsApptAtShoob}
              setHasSelectedDay={setHasSelectedDay}
            />
          ) : (
            <div className={classes.pickerEmpty}>
              <p>No appointment times are available for your school right now. Please check back soon or contact us at <a href="mailto:info@shoobphoto.com">info@shoobphoto.com</a>.</p>
            </div>
          )}
          <button
            type="button"
            className={cn('seniors-btn', 'seniors-btn--primary', 'seniors-btn--lg', classes.seniorContinue)}
            disabled={!appt}
            onClick={handleContinue}
          >
            {isReschedule ? 'Reschedule' : 'Continue'}
          </button>
        </div>
        <div className={classes.seniorInfo}>
          <DressCodeInfo school={customer.currentSchool} />
        </div>
      </div>
    )
  }

  // Default (elementary / retake) flow — unchanged.
  return (
    <>
      <ToastContainer position="bottom-left" />
      <AttentionBanner content="Step 2. Choose Your Appointment Time" />
      <div className={classes.container}>
        <p className={classes.portraitTitle}>{`${customer.currentSchool.name} Portraits`}</p>
        <div className={classes.apptCard}>
          <img src={apptImgElem} alt="Appointment Image" />
          <div className={classes.apptCardContent}>
            <ShootingSchedules
              school={customer.currentSchool}
              schedules={shootingSchedules}
              setAppt={setAppt}
              selectedOption={customer.currentOrderPackage.option}
              setIsApptAtShoob={setIsApptAtShoob}
            />
            <Button
              classes={classes.btn}
              size="large"
              variant="primary"
              disabled={!appt}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </div>
        </div>
        <AppointmentLocationInfo school={customer.currentSchool} />
        <DressCodeInfo school={customer.currentSchool} />
      </div>
    </>
  )
}

export default AppointmentSelection
